<script>
  // The company comes first (Miles 2026-11-05). Everything typed in the builder belongs to a company: one you run, or
  // a new one. A company is built from its owner's ONE dream (one design per company; it grows and changes).
  //  · A design with no company (older ones) asks "Which company is this for?" — attach to one you run
  //    (fn_bb_attach_company) or make a new one (fn_bb_make_company). Sketched offers become that company's HQ drafts.
  //  · A company design shows where you are designing and "See the map" (the map lives outside the builder tabs and
  //    is reached from HQ too).
  // Contractors never design for a company: the database only lets the owner and key people in (fn_design_company_ok).
  import { supabase } from '../lib/supabase.js';
  import { workspace, loadWorkspace } from '../lib/stores/workspace.js';
  import { showToast } from '../lib/stores/toast.js';

  let companies = $state([]);
  let pick = $state('');          // partner id, or 'new'
  let name = $state(''), city = $state('');
  let busy = $state(false), error = $state('');

  $effect(() => { workspace.id; loadCompanies(); });
  async function loadCompanies() {
    const { data } = await supabase.rpc('fn_bb_my_companies');
    companies = data || [];
    if (!pick) pick = companies.find((c) => !c.designs)?.partner_id || 'new';
  }
  const here = $derived(companies.find((c) => c.partner_id === workspace.partner_id) || null);

  async function run(fn) {
    if (busy) return;
    busy = true; error = '';
    const guard = setTimeout(() => { busy = false; }, 30000);   // a hung call must never strand the button
    try { await fn(); } catch (e) { error = e?.message || String(e); }
    finally { clearTimeout(guard); busy = false; }
  }

  const useCompany = () => run(async () => {
    let partner = pick;
    if (pick === 'new') {
      if (!name.trim()) throw new Error('Give the company a name.');
      const { data, error: e } = await supabase.rpc('fn_bb_make_company', { p_workspace_id: workspace.id, p_name: name.trim(), p_city: city.trim() || null });
      if (e) throw e;
      partner = data?.partner_id;
    } else {
      const { error: e } = await supabase.rpc('fn_bb_attach_company', { p_workspace_id: workspace.id, p_partner_id: pick });
      if (e) throw e;
    }
    await loadWorkspace(workspace.id);
    if (workspace.partner_id !== partner) throw new Error('Saved, but this design did not attach. Reload the page.');
    showToast('This design now belongs to the company', 'success');
  });

  const mapUrl = $derived(workspace.partner_id ? `?map=1&company=${workspace.partner_id}` : '?map=1');
</script>

{#if workspace.id}
  <div class="cp">
    {#if !workspace.partner_id}
      <div class="t">Which company is this for?</div>
      <div class="s">Everything you make here belongs to a company — one you run, or a new one.</div>
      <select bind:value={pick} disabled={busy}>
        {#each companies.filter((c) => !c.designs) as c (c.partner_id)}<option value={c.partner_id}>{c.name}</option>{/each}
        <option value="new">New company…</option>
      </select>
      {#if pick === 'new'}
        <input placeholder="Company name" bind:value={name} disabled={busy} />
        <input placeholder="City (optional)" bind:value={city} disabled={busy} />
      {/if}
      <button class="go" type="button" onclick={useCompany} disabled={busy}>{busy ? 'Saving…' : 'Use this company'}</button>
    {:else}
      <div class="row">
        <div class="txt">
          <div class="t">Designing in {here?.name || 'your company'}</div>
          <div class="s">{workspace.name}</div>
        </div>
        <a class="link" href={mapUrl}>See the map</a>
      </div>
    {/if}
    {#if error}<div class="err">{error}</div>{/if}
  </div>
{/if}

<style>
  .cp { border: 1px solid rgba(122,184,255,0.45); border-radius: 14px; padding: 14px 16px; margin: 0 0 24px;
    background: rgba(74,144,226,0.06); display: flex; flex-direction: column; gap: 8px; }
  .row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
  .txt { flex: 1; min-width: 160px; }
  .t { font-size: 15px; font-weight: 600; color: var(--text); }
  .s { font-size: 13px; color: var(--text-muted); }
  select, input { background: rgba(0,0,0,0.35); border: 1px solid rgba(255,255,255,0.14); border-radius: 10px; color: var(--text);
    font: inherit; font-size: 14px; padding: 9px 12px; }
  .go { align-self: flex-start; background: #4A90E2; color: #fff; border: none; border-radius: 999px; padding: 9px 18px; font: inherit;
    font-size: 14px; font-weight: 600; cursor: pointer; }
  .go:disabled { opacity: 0.5; }
  .ghost { background: transparent; border: 1px solid rgba(255,255,255,0.2); color: var(--text); border-radius: 999px; padding: 7px 14px;
    font: inherit; font-size: 13px; cursor: pointer; }
  .link { color: #7ab8ff; font-size: 13px; text-decoration: none; }
  .err { color: #ff8a80; font-size: 13px; }
</style>
